import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIconSmall, MinusIconSmall } from "../../assets/icons";
import LoadingSpinner from "../loading-spinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import useUserStore from "../../store/userStore";
import useModalsStore from "../../store/modalsStore";
import HeaderLogo from "../../assets/header-logo";
import { getCurrency } from "../../lib/middleware";
// import trackTikTokSafe from "../TiktokPixelTracker";

const BottomSection = ({ data, cartAttributes, subProductID }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const setModalOpen = useModalsStore((state) => state.setModalOpen);
    const setModalContent = useModalsStore((state) => state.setModalContent);
    const setLoginModal = useModalsStore((state) => state.setLoginModalOpen);
    const user = useUserStore((state) => state.user);

    const minQuantity = parseInt(data?.quantity_settings?.min, 10) || 1;
    const [quantity, setQuantity] = useState(minQuantity);
    const currency = getCurrency();
    const [finalPrice, setFinalPrice] = useState(parseFloat(data.price) || 0);
    const [loading, setLoading] = useState(false);

    // 🔹 Add to cart handler
    const addToCart = async () => {
        if (!user) {
            toast.error(t("messages.login-cart"));
            setLoginModal(true);
            return;
        }

        if (
            data.sub_product &&
            data.sub_product.length > 0 &&
            subProductID === 0
        ) {
            toast.error(`${t("fill-fields")} : ${t("available-weight")}`);
            return;
        }

        if (data.attributes && data.attributes.length > 0) {
            const requiredAttributes = data.attributes.filter(
                (attr) => attr.is_required === 1,
            );
            const missingAttributes = requiredAttributes.filter(
                (attr) => !cartAttributes[attr.id],
            );

            if (missingAttributes.length > 0) {
                toast.error(
                    `${t("fill-fields")} : ${missingAttributes
                        .map((attr) => attr.name)
                        .join(", ")}`,
                );
                return;
            }
        }

        setLoading(true);
        const attributes = Object.values(cartAttributes);
        const attributesWithQuantity = attributes.map((attr) => ({
            ...attr,
            quantity: attr.quantity || quantity, // use existing quantity if present, otherwise product quantity
        }));

        // 🔹 Base price
        let basePrice = parseFloat(data.price) || 0;
        const selectedSubProduct = data.sub_product?.find(
            (sub) => sub.id === subProductID,
        );
        if (selectedSubProduct && selectedSubProduct.price) {
            basePrice = parseFloat(selectedSubProduct.price) || basePrice;
        }

        // 🔹 Attribute price handling
        let foamPrice = 0;
        let extraPrice = 0;

        attributes.forEach((attr) => {
            if (attr && attr.price) {
                const price = parseFloat(attr.price) || 0;
                if (attr.section_name === "list-single-price") {
                    extraPrice += price;
                } else {
                    extraPrice += price * quantity;
                }
                foamPrice += price;
            }
        });

        const totalPrice = basePrice * quantity;
        const subTotal = totalPrice + extraPrice;

        const cartProduct = {
            department_id: data.department.id,
            category_id: data.category.id,
            product_id: data.id,
            sub_product_id: subProductID == 0 ? null : subProductID,
            quantity: quantity,
            shop_id: data.shop?.id || null,
            attributes: attributesWithQuantity,

            //
            subtotal: subTotal,
            sub_total: subTotal,
            total_price: totalPrice,
            foam_price: foamPrice.parseFloat || 0,
            //
        };

        try {
            await axios.post("/cart/add", cartProduct);

            // --- Meta Pixel AddToCart Event
            if (typeof fbq !== "undefined") {
                // eslint-disable-next-line no-undef
                fbq("track", "AddToCart", {
                    content_name: data.name,
                    content_ids: [data.id],
                    content_type: "product",
                    value: finalPrice,
                    currency: currency || "AED",
                    quantity,
                    shop_id: data.shop?.id,
                });
            }

            // --- Snap Pixel AddToCart Event
            if (typeof snaptr !== "undefined") {
                // eslint-disable-next-line no-undef
                snaptr("track", "ADD_CART", {
                    item_name: data.name,
                    content_ids: [data.id],
                    price: finalPrice,
                    currency: currency || "AED",
                    quantity: quantity,
                    shop_id: data.shop?.id,
                });
            }

            // --- TikTok Pixel AddToCart Event
            if (window.ttq) {
                window.ttq.track("AddToCart", {
                    content_name: data.name,
                    content_ids: [data.id],
                    content_type: "product",
                    value: finalPrice,
                    currency: currency || "AED",
                    quantity,
                    shop_id: data.shop?.id,
                });
            }

            toast.success(t("messages.added-to-cart"));
            setModalOpen(true);
            setModalContent({
                confirmText: t("go-to-cart"),
                cancelText: t("continue-shopping"),
                title: t("new-product-added"),
                onCancel() {
                    setModalOpen(false);
                    navigate("/");
                },
                onConfirm() {
                    setModalOpen(false);
                    navigate("/cart");
                },
                icon: <HeaderLogo />,
                content: (
                    <div className="grid grid-cols-2 text-xl font-medium text-primary">
                        <span>{data.name}</span>
                        <span>
                            {finalPrice.toFixed(2)} {t(currency)}
                        </span>
                    </div>
                ),
            });
        } catch (error) {
            toast.error(t("messages.failed-to-add"));
        }
        setLoading(false);
    };

    // 🔹 Calculate final price dynamically
    useEffect(() => {
        let updatedPrice = parseFloat(data.price) || 0;

        // Override base price with sub-product price
        const selectedSubProduct = data.sub_product?.find(
            (sub) => sub.id === subProductID,
        );
        if (selectedSubProduct && selectedSubProduct.price) {
            updatedPrice = parseFloat(selectedSubProduct.price) || updatedPrice;
        }

        // Handle attributes with section_name rules
        let attributeExtra = 0;
        console.log("Cart Attributes:", cartAttributes);
        Object.values(cartAttributes).forEach((attr) => {
            console.log("Attribute:", attr);
            if (attr && attr.price) {
                const price = parseFloat(attr.price) || 0;
                if (attr.section_name === "list-single-price") {
                    attributeExtra += price;
                } else {
                    attributeExtra += price * quantity;
                }
            }
        });

        setFinalPrice(updatedPrice * quantity + attributeExtra);
    }, [
        subProductID,
        cartAttributes,
        quantity,
        data.price,
        data.sub_product,
        data.attributes,
    ]);

    return (
        <div className="mx-auto flex max-w-[852px] flex-col px-4 pb-28 pt-4">
            <div className="flex flex-col gap-y-4">
                <div className="flex items-center justify-between">
                    <span className="text-[22px] font-medium text-[rgba(23,23,23,1)]">
                        {t("quantity")}
                    </span>
                    <div className="flex items-center gap-x-2.5">
                        <button
                            className="rounded-md bg-[rgba(100,140,116,1)] p-2 transition hover:scale-105"
                            onClick={() => {
                                setQuantity((prev) =>
                                    prev > minQuantity ? prev - 1 : minQuantity,
                                );
                            }}
                        >
                            <MinusIconSmall />
                        </button>
                        <span className="text-lg font-medium text-[rgba(24,24,27,1)]">
                            {quantity}
                        </span>
                        <button
                            className="rounded-md bg-[rgba(100,140,116,1)] p-2 transition hover:scale-105"
                            onClick={() => {
                                setQuantity(quantity + 1);
                            }}
                        >
                            <PlusIconSmall />
                        </button>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-[22px] font-medium text-[rgba(23,23,23,1)]">
                        {t("price")}
                    </span>
                    <div className="text-lg text-[rgba(24,24,27,1)]">
                        {finalPrice.toFixed(2)} {t(currency || "AED")}
                    </div>
                </div>
                <button
                    disabled={data.stock === 0}
                    onClick={addToCart}
                    className={`flex items-center justify-center rounded-lg py-[18px] text-white transition 
    ${
        data.stock === 0
            ? "cursor-not-allowed bg-gray-300"
            : "bg-primary hover:opacity-80"
    }
  `}
                >
                    {loading ? (
                        <LoadingSpinner />
                    ) : data.stock === 0 ? (
                        t("outofstock")
                    ) : (
                        t("add-to-cart")
                    )}
                </button>
            </div>
        </div>
    );
};

export default BottomSection;
